import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface TabDef {
  key: string;
  label: string;
}

/**
 * WAI-ARIA tabs: a `tablist` of `tab` buttons with a roving tabindex (Left/Right/Home/End move and activate), each controlling a
 * `tabpanel`. The active key is owned by the caller so it can live in the URL.
 */
export function Tabs({ tabs, active, onChange, idPrefix, label, children }: {
  tabs: TabDef[];
  active: string;
  onChange: (key: string) => void;
  idPrefix: string;
  label: string;
  children: ReactNode;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const index = tabs.findIndex((t) => t.key === active);
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    const target = tabs[next]!;
    onChange(target.key);
    refs.current[target.key]?.focus();
  }

  return (
    <div className="jr-tabs">
      <div className="jr-tabs__list" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {tabs.map((t) => (
          <button
            key={t.key}
            ref={(el) => { refs.current[t.key] = el; }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${t.key}`}
            aria-selected={t.key === active}
            aria-controls={`${idPrefix}-panel-${t.key}`}
            tabIndex={t.key === active ? 0 : -1}
            className={t.key === active ? 'jr-tab jr-tab--active' : 'jr-tab'}
            onClick={() => onChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${idPrefix}-panel-${active}`} aria-labelledby={`${idPrefix}-tab-${active}`} tabIndex={0} className="jr-tabs__panel">
        {children}
      </div>
    </div>
  );
}
